import { HttpClient } from '@angular/common/http';
import {
    Directive, ElementRef,
    EventEmitter, forwardRef, Input, NgZone, Output
} from '@angular/core';
import {
    FormControl, NG_VALUE_ACCESSOR
} from '@angular/forms';
import { googleRequest } from '../api/models/googleRequest';
import { ValidateCaptchaRequest } from '../api/models/ValidateCaptchaRequest';
import { ValidateCaptchaResponse } from '../api/models/ValidateCaptchaResponse';
import { ApiErrorCode, ENVIRONMENTS, RECAPTCHA_SITE_KEY_PROD, RECAPTCHA_SITE_KEY_TEST, ServiceURLConstants } from '../constants/upplm.constants';
import { UpplmApiService } from '../services/upplm.api.services';
import { UpsCommonService } from '../services/upplm.common.service';
import { ConfigurationService } from '../services/upplm.configuration.service';
import { GoogleRequestService } from '../services/upplm.googleRequest.service';


declare const grecaptcha: any;

declare global {
    interface Window {
        grecaptcha: any;
        reCaptchaLoad: () => void
    }
}

export interface ReCaptchaConfig {
    theme?: 'dark' | 'light';
    type?: 'audio' | 'image';
    size?: 'compact' | 'normal';
    tabindex?: number;
}

@Directive({
    selector: '[nbRecaptcha]',
    exportAs: 'nbRecaptcha',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ReCaptchaDirective),
            multi: true
        },
    ]
})
export class ReCaptchaDirective {
    @Input() key: string;
    @Input() config: ReCaptchaConfig = {};
    @Input() lang: string;

    @Output() captchaResponse = new EventEmitter<string>();
    @Output() captchaExpired = new EventEmitter();
    @Output() captchaResponseBol: boolean = false;

    private control: FormControl;
    private widgetId: number;

    public valueKey: any = false;

    private onChange: (value: string) => void;
    private onTouched: (value: string) => void;
    siteKey: string = RECAPTCHA_SITE_KEY_TEST;

    constructor(
        private element: ElementRef,
        private ngZone: NgZone, 
        private http: HttpClient,
        private _commonService: UpsCommonService,
        private _googleRequestService: GoogleRequestService,
        private _apiService: UpplmApiService,
        private _configurationService: ConfigurationService

    ) {
    }

    /**
     * Useful for multiple captcha
     * @returns {number}
     */
    getId() {
        return this.widgetId;
    }

    writeValue(obj: any): void {
        /* empty */
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * onExpired
     */
    onExpired() {
        this.ngZone.run(() => {
            this.captchaExpired.emit();
            this.onChange(null);
            this.onTouched(null);
        });
    }

    /**
     *
     * @param response
     */
    onSuccess() {
        if (this._configurationService.appSettings.environment === ENVIRONMENTS.MAHWAH || this._configurationService.appSettings.environment === ENVIRONMENTS.WINDWARD) {
            this.siteKey = RECAPTCHA_SITE_KEY_PROD;
        }

        return new Promise((resolve, reject) => {
            grecaptcha.enterprise.ready(async () => {

                const token = await grecaptcha.enterprise.execute(this.siteKey, {action: 'create_placard'});
                this.ngZone.run(() => {
                    this.validateCaptcha(token).then(
                        response => {
                            this.onChange(token);
                            this.onTouched(token);
                            if (this.captchaResponseBol) {
                                resolve(true);
                            } else {
                                resolve(false);
                            }
                        });
                });

            }); 
            }
    
        );
    }

    validateCaptcha(token: string) {
        return new Promise((resolve, reject) => {
            let request = new ValidateCaptchaRequest();
            request.captchaResponse = token;
            this._apiService.PostServiceAPI(this._commonService.createURL(ServiceURLConstants.VALIDATE_CAPTCHA, ApiErrorCode.VALIDATE_CAPTCHA), request).subscribe((response: ValidateCaptchaResponse) => {
                let validateResponse: ValidateCaptchaResponse = response;
                this.captchaResponseBol = (validateResponse.status === 'Success') ? true : false;
                resolve(true);
            });
        });
    }

    /**
     *
     * @param token
     */
    verifyToken(token: string) {
        let request: googleRequest = new googleRequest();
        request.googleKey = token;
        let angularUrl: any = window.location.href;
        let url = angularUrl.split('#')[0] + '/getCaptchaPublicKey/';

        this._googleRequestService.getGoogleRequest(url, request).subscribe((response: any) => {

            switch (response) {
                case "success":
                    this.valueKey = true;
                    break;
                case "error":
                    this.valueKey = false;
                    break;
                case "exception":
                    this.valueKey = false;
                    console.log("exception in controller getCaptchaPublicKey")
                    break;
                default:
                    this.valueKey = false;
                    break;
            }
        });
    }

    /**
     * Renders the container as a reCAPTCHA widget and returns the ID of the newly created widget.
     * @param element
     * @param config
     * @returns {number}
     */
    private render(element: HTMLElement, config): number {
        return grecaptcha.enterprise.render(element, config);
    }

    /**
     * Resets the reCAPTCHA widget.
     */
    reset(): void {
        if (!this.widgetId) return;
        grecaptcha.enterprise.reset(this.widgetId);
        this.onChange(null);
    }

    /**
     * Gets the response for the reCAPTCHA widget.
     * @returns {string}
     */
    getResponse(): string {
        if (!this.widgetId)
            return grecaptcha.enterprise.getResponse(this.widgetId);
    }
}